import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    Plane, PlaneTakeoff, Users, Wrench, AlertTriangle,
    Clock, CheckCircle, XCircle, TrendingUp, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
    const { user, airline } = useAuth();
    const [stats, setStats] = useState(null);
    const [todayFlights, setTodayFlights] = useState([]);
    const [urgentMessages, setUrgentMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            // FASE 4: pedir solo vuelos del dia (backend soporta ?date=)
            const today = getLocalToday();
            const [statsRes, flightsRes, crewRes, urgentRes] = await Promise.all([
                axios.get(`${API}/statistics/overview`, { params: { local_date: today } }),
                axios.get(`${API}/flights?date=${today}`),
                axios.get(`${API}/crew`).catch(() => ({ data: [] })),
                axios.get(`${API}/op-messages?priority=high`).catch(() => ({ data: [] })),
            ]);
            setUrgentMessages((urgentRes.data || []).filter(m => ['high','urgent','critical'].includes(m.priority)).slice(0, 5));
            setStats(statsRes.data);
            let todayFlightsList = flightsRes.data;
            // Pilot only sees assigned flights
            if (user?.role === 'pilot') {
                // FASE 5: preferir user_id (link real) sobre email
                const pilotCrew = crewRes.data.find(c => c.user_id === user.id) 
                              || crewRes.data.find(c => c.email === user.email);
                if (pilotCrew) {
                    todayFlightsList = todayFlightsList.filter(f =>
                        f.assigned_crew?.some(ac => ac.crew_id === pilotCrew.id)
                    );
                }
            }
            setTodayFlights(todayFlightsList.slice(0, 5));
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('No se pudo cargar el dashboard. Reintenta en unos segundos.');
            toast.error('Error al cargar datos del dashboard');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            scheduled: { label: 'Programado', className: 'status-info' },
            boarding: { label: 'Abordando', className: 'status-warning' },
            departed: { label: 'Despegado', className: 'status-success' },
            en_route: { label: 'En Vuelo', className: 'status-success' },
            arrived: { label: 'Llegado', className: 'status-success' },
            delayed: { label: 'Demorado', className: 'status-warning' },
            cancelled: { label: 'Cancelado', className: 'status-danger' }
        };
        const config = statusConfig[status] || { label: status, className: '' };
        return <Badge className={config.className}>{config.label}</Badge>;
    };

    const formatTime = (dateStr) => {
        return new Date(dateStr).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
    };

    const MetricCard = ({ icon: Icon, value, label, trend, color = "primary" }) => (
        <Card className="card-aviation metric-card-hover" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="metric-value font-mono">{value}</p>
                        <p className="metric-label">{label}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg bg-${color}/10 flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 text-${color}`} />
                    </div>
                </div>
                {trend && (
                    <div className="mt-4 flex items-center gap-1 text-xs text-emerald-500">
                        <TrendingUp className="w-3 h-3" />
                        <span>{trend}</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <div className="space-y-6 animate-fade-in" data-testid="dashboard-loading">
                <div className="h-8 w-48 bg-muted rounded skeleton-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-32 bg-muted rounded-lg skeleton-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="dashboard">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        Dashboard
                    </h1>
                    <p className="text-muted-foreground">
                        Bienvenido, {user?.first_name}. Centro de control de {airline?.name || 'operaciones'}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date().toLocaleDateString('es', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard 
                    icon={Plane}
                    value={stats?.today_flights || 0}
                    label="Vuelos Hoy"
                    color="primary"
                />
                <MetricCard 
                    icon={PlaneTakeoff}
                    value={`${stats?.operational_aircraft || 0}/${stats?.total_aircraft || 0}`}
                    label="Flota Operativa"
                    color="primary"
                />
                <MetricCard 
                    icon={Users}
                    value={stats?.total_crew || 0}
                    label="Tripulantes"
                    color="primary"
                />
                <MetricCard 
                    icon={AlertTriangle}
                    value={stats?.active_deferrals || 0}
                    label="Diferidos Activos"
                    color="amber-500"
                />
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard 
                    icon={Wrench}
                    value={stats?.pending_maintenance || 0}
                    label="Mantenimientos Pendientes"
                    color="primary"
                />
                <MetricCard 
                    icon={CheckCircle}
                    value={stats?.flight_status_breakdown?.arrived || 0}
                    label="Vuelos Completados"
                    color="emerald-500"
                />
                <MetricCard 
                    icon={XCircle}
                    value={stats?.flight_status_breakdown?.cancelled || 0}
                    label="Vuelos Cancelados"
                    color="red-500"
                />
            </div>

            {/* Auditoria 7 - Item 17: Urgent operational messages panel */}
            {urgentMessages.length > 0 && (
                <Card className="card-aviation border-red-500/40 bg-red-500/5" data-testid="urgent-messages-panel">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] flex items-center gap-2 text-red-400">
                            <span className="animate-pulse">●</span>
                            Mensajes operacionales urgentes ({urgentMessages.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {urgentMessages.map(m => (
                            <div key={m.id} className="p-2 rounded border border-red-500/30 bg-red-500/5 text-sm" data-testid={`urgent-msg-${m.id}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-red-400 uppercase text-xs">{m.priority}</span>
                                    <span className="text-[10px] text-muted-foreground font-mono">{(m.created_at || '').slice(0, 16)}</span>
                                </div>
                                <p className="font-semibold mt-1">{m.title || m.subject}</p>
                                <p className="text-xs text-muted-foreground">{m.body || m.content || m.message}</p>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Today's Flights */}
            <Card className="card-aviation" data-testid="today-flights-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        Vuelos de Hoy
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {todayFlights.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                            No hay vuelos programados para hoy
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table-aviation">
                                <thead>
                                    <tr>
                                        <th>Vuelo</th>
                                        <th>Ruta</th>
                                        <th>Salida</th>
                                        <th>Llegada</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {todayFlights.map((flight) => (
                                        <tr key={flight.id} className="table-row-enter" data-testid={`flight-row-${flight.flight_number}`}>
                                            <td className="font-mono font-semibold text-primary">
                                                {flight.flight_number}
                                            </td>
                                            <td>
                                                <span className="font-mono">{flight.origin}</span>
                                                <span className="mx-2 text-muted-foreground">→</span>
                                                <span className="font-mono">{flight.destination}</span>
                                            </td>
                                            <td className="font-mono">{formatTime(flight.scheduled_departure)}</td>
                                            <td className="font-mono">{formatTime(flight.scheduled_arrival)}</td>
                                            <td>{getStatusBadge(flight.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Quick Actions by Role */}
            <Card className="card-aviation" data-testid="quick-actions-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-xl font-bold uppercase font-['Barlow_Condensed']">
                        Acciones Rápidas
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {user?.role === 'admin' || user?.role === 'dispatcher' || user?.role === 'ccod' ? (
                            <Button 
                                variant="outline" 
                                className="quick-action-btn h-auto py-4 flex-col gap-2"
                                onClick={() => window.location.href = '/flights'}
                                data-testid="quick-action-flights"
                            >
                                <Plane className="w-6 h-6" />
                                <span className="text-xs">Gestionar Vuelos</span>
                            </Button>
                        ) : null}
                        {user?.role === 'admin' || user?.role === 'pilot' ? (
                            <Button 
                                variant="outline" 
                                className="quick-action-btn h-auto py-4 flex-col gap-2"
                                onClick={() => window.location.href = '/logbook'}
                                data-testid="quick-action-logbook"
                            >
                                <Clock className="w-6 h-6" />
                                <span className="text-xs">Bitácora</span>
                            </Button>
                        ) : null}
                        {user?.role === 'admin' || user?.role === 'maintenance' ? (
                            <Button 
                                variant="outline" 
                                className="quick-action-btn h-auto py-4 flex-col gap-2"
                                onClick={() => window.location.href = '/maintenance'}
                                data-testid="quick-action-maintenance"
                            >
                                <Wrench className="w-6 h-6" />
                                <span className="text-xs">Mantenimiento</span>
                            </Button>
                        ) : null}
                        {user?.role === 'admin' || user?.role === 'crew_admin' ? (
                            <Button 
                                variant="outline" 
                                className="quick-action-btn h-auto py-4 flex-col gap-2"
                                onClick={() => window.location.href = '/crew'}
                                data-testid="quick-action-crew"
                            >
                                <Users className="w-6 h-6" />
                                <span className="text-xs">Tripulaciones</span>
                            </Button>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
